"use client";

/**
 * L'ONGLET « RATTACHEMENT ».
 *
 * Les virements reçus sur Qonto que MyFacture n'a pas su rattacher seul. Pour
 * chacun : les factures qu'il propose, une recherche pour en choisir une
 * autre, ou « Ignorer » quand le virement ne paie aucune facture. Rattacher
 * enregistre le paiement, passe la facture à « Payée » s'il la solde, et joint
 * son PDF à l'opération Qonto.
 *
 * Même composant sur ordinateur et sur téléphone.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import Link from "next/link";
import { useData } from "@/components/data-provider";
import {
  etatRattachement,
  rattacherVirement,
  ignorerVirementQonto,
  synchroniserQontoMaintenant,
  rattraperQontoMaintenant,
  type EtatRattachement,
} from "@/lib/actions/qonto";
import { toast } from "sonner";
import { Link2, Loader2, RefreshCw, Search, EyeOff, Check, AlertCircle, CheckCircle2, History } from "lucide-react";

const euros = (n: number) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const jour = (iso: string) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

const plat = (t: string) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

type Virement = EtatRattachement["virements"][number];
type Facture = EtatRattachement["factures"][number];

function CarteVirement({
  v,
  factures,
  occupe,
  onRattacher,
  onIgnorer,
}: {
  v: Virement;
  factures: Facture[];
  occupe: string | null;
  onRattacher: (virementId: string, factureId: string, numero: string) => void;
  onIgnorer: (virementId: string) => void;
}) {
  const [recherche, setRecherche] = useState("");
  const [ouvert, setOuvert] = useState(v.propositions.length === 0);

  const trouvees = useMemo(() => {
    const q = plat(recherche.trim());
    const montant = Math.round(v.montant * 100);
    const liste = q
      ? factures.filter((f) => plat(`${f.numero} ${f.client} ${f.resteDu.toFixed(2)} ${f.resteDu.toFixed(2).replace(".", ",")}`).includes(q))
      : // Sans recherche : d'abord celles dont le montant est exactement celui reçu, en attente avant déjà payées.
        [...factures].sort(
          (a, b) =>
            Number(Math.round(b.resteDu * 100) === montant) - Number(Math.round(a.resteDu * 100) === montant) ||
            Number(a.payee) - Number(b.payee)
        );
    return liste.slice(0, 8);
  }, [recherche, factures, v.montant]);

  const enCours = occupe?.startsWith(v.virementId) ?? false;

  return (
    <li className="glass-card rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-lg font-bold text-emerald-400 tabular-nums">+{euros(v.montant)}</p>
          <p className="text-sm font-medium text-foreground truncate">{v.libelle || "Virement reçu"}</p>
          {v.reference && <p className="text-xs text-muted-foreground break-words">{v.reference}</p>}
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">{jour(v.date)}</span>
      </div>

      {v.propositions.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-amber-400">MyFacture propose</p>
          {v.propositions.map((p) => (
            <button
              key={p.factureId}
              type="button"
              disabled={enCours}
              onClick={() => onRattacher(v.virementId, p.factureId, p.numero)}
              className="w-full flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-left hover:bg-amber-500/10 disabled:opacity-50 touch-manipulation"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Facture {p.numero} · {p.client}</p>
                <p className="text-xs text-muted-foreground">Reste dû {euros(p.resteDu)} · {p.raison}</p>
              </div>
              {occupe === `${v.virementId}:${p.factureId}` ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <Check className="h-4 w-4 text-emerald-400 shrink-0" />}
            </button>
          ))}
        </div>
      )}

      {ouvert ? (
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="N° de facture, client ou montant"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-background text-sm text-foreground"
            />
          </div>
          {trouvees.length === 0 ? (
            <p className="text-xs text-muted-foreground px-1">Aucune facture en attente ne correspond.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {trouvees.map((f) => {
                const exact = Math.round(f.resteDu * 100) === Math.round(v.montant * 100);
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      disabled={enCours}
                      onClick={() => onRattacher(v.virementId, f.id, f.numero)}
                      className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted disabled:opacity-50 touch-manipulation"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground truncate">
                          {f.numero} · {f.client}
                          {f.payee && <span className="ml-2 text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-sky-500/10 text-sky-400">déjà payée</span>}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {jour(f.dateEmission)} · {f.payee ? "montant" : "reste dû"} <span className={exact ? "text-emerald-400 font-medium" : ""}>{euros(f.resteDu)}</span>
                          {!exact && !f.payee && (f.resteDu > v.montant ? " · paiement partiel" : " · montant supérieur au reste dû")}
                        </p>
                      </div>
                      {occupe === `${v.virementId}:${f.id}` ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
        <button type="button" onClick={() => setOuvert(true)} className="text-sm text-blue-500 hover:text-blue-400 font-medium">
          Choisir une autre facture
        </button>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          disabled={enCours}
          onClick={() => onIgnorer(v.virementId)}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
        >
          {occupe === `${v.virementId}:ignorer` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <EyeOff className="h-3.5 w-3.5" />}
          Ne paie aucune facture
        </button>
      </div>
    </li>
  );
}

const aujourdhuiISO = () => new Date().toISOString().slice(0, 10);
const debutAnneeISO = () => `${new Date().getFullYear()}-01-01`;

function BlocRattrapage({
  rattrapage,
  onLancer,
  onRattacher,
  occupe,
}: {
  rattrapage: EtatRattachement["rattrapage"];
  onLancer: (debut: string, fin: string) => Promise<void>;
  onRattacher: (virementId: string, factureId: string, numero: string) => void;
  occupe: string | null;
}) {
  const [debut, setDebut] = useState(debutAnneeISO());
  const [fin, setFin] = useState(aujourdhuiISO());
  const [enCours, setEnCours] = useState(false);

  const lancer = async () => {
    setEnCours(true);
    try {
      await onLancer(debut, fin);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <section className="glass-card rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-sky-400" />
        <h2 className="text-sm font-semibold text-foreground">Rattrapage des factures déjà payées</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Pour les factures déjà en « Payée » dans MyFacture : MyFacture retrouve leur virement dans Qonto et y joint le PDF. Le statut et les paiements enregistrés ne changent pas.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">Factures émises ou payées du</span>
          <input type="date" value={debut} max={fin} onChange={(e) => setDebut(e.target.value)} className="block h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground" />
        </label>
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">au</span>
          <input type="date" value={fin} min={debut} max={aujourdhuiISO()} onChange={(e) => setFin(e.target.value)} className="block h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground" />
        </label>
        <button
          type="button"
          onClick={lancer}
          disabled={enCours || !debut || !fin}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
        >
          {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
          Lancer le rattrapage
        </button>
      </div>

      {rattrapage && (
        <div className="space-y-2 pt-1">
          <p className="text-xs text-muted-foreground">
            Dernier rattrapage ({jour(rattrapage.periode.debut)} → {jour(rattrapage.periode.fin)}) :{" "}
            {rattrapage.erreur ? (
              <span className="text-red-400">{rattrapage.erreur}</span>
            ) : (
              <>
                {rattrapage.factures} facture(s) payée(s) sans virement · {rattrapage.liees.length} liée(s) · {rattrapage.justificatifs.joints} PDF joint(s) dans Qonto
                {rattrapage.aConfirmer.length ? ` · ${rattrapage.aConfirmer.length} à confirmer` : ""}
              </>
            )}
          </p>
          {rattrapage.comptesLus && rattrapage.comptesLus.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Comptes lus : {rattrapage.comptesLus.map((c) => `${c.nom}${c.externe ? " (autre banque)" : ""}`).join(" · ")}
            </p>
          )}
          {rattrapage.erreursComptes && rattrapage.erreursComptes.length > 0 && (
            <p className="text-xs text-amber-400">Compte non lu : {rattrapage.erreursComptes.join(" · ")}</p>
          )}
          {rattrapage.ecartsRegies && rattrapage.ecartsRegies.length > 0 && (
            <ul className="text-xs text-amber-400 space-y-0.5">
              {rattrapage.ecartsRegies.map((e) => (
                <li key={`${e.date}-${e.recu}`}>
                  Virement TF1 du {jour(e.date)} : {euros(e.recu)} reçus, {euros(e.attendu)} attendus ({euros(e.autofactures)} d'autofactures − {euros(e.retenues)} de factures Les Indés retenues, échéance {jour(e.echeance)}). Écart {euros(e.recu - e.attendu)} — rien n'a été rattaché.
                </li>
              ))}
            </ul>
          )}
          {rattrapage.stripe && <p className="text-xs text-amber-400">Versements Stripe non lus : {rattrapage.stripe}</p>}
          {rattrapage.justificatifs.erreurs.length > 0 && (
            <p className="text-xs text-amber-400">Justificatif non joint : {rattrapage.justificatifs.erreurs.slice(0, 3).join(" · ")}</p>
          )}
          {rattrapage.liees.length > 0 && (
            <ul className="text-xs text-muted-foreground list-disc list-inside">
              {rattrapage.liees.slice(0, 20).map((l) => (
                <li key={`${l.numero}-${l.date}`}>{l.numero} — {euros(l.montant)} reçu le {jour(l.date)}</li>
              ))}
              {rattrapage.liees.length > 20 && <li>… et {rattrapage.liees.length - 20} autre(s)</li>}
            </ul>
          )}
          {rattrapage.aConfirmer.length > 0 && (
            <ul className="space-y-2">
              {rattrapage.aConfirmer.map((p) => (
                <li key={`${p.virementId}:${p.factureId}`}>
                  <button
                    type="button"
                    disabled={occupe !== null}
                    onClick={() => onRattacher(p.virementId, p.factureId, p.numero)}
                    className="w-full flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-left hover:bg-amber-500/10 disabled:opacity-50 touch-manipulation"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">
                        {euros(p.montant)} du {jour(p.date)} — {p.libelle}
                      </p>
                      <p className="text-xs text-muted-foreground">→ Facture {p.numero} · {p.client} · {p.raison}</p>
                    </div>
                    {occupe === `${p.virementId}:${p.factureId}` ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <Check className="h-4 w-4 text-emerald-400 shrink-0" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

export function Rattachement() {
  const { societe } = useData();
  const [etat, setEtat] = useState<EtatRattachement | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);
  const [actualisation, setActualisation] = useState(false);

  const charger = useCallback(async () => {
    if (!societe?.id) return;
    const res = await etatRattachement(societe.id);
    if (res.success) setEtat(res.data);
    else toast.error(res.error);
  }, [societe?.id]);

  useEffect(() => { charger(); }, [charger]);

  const actualiser = async () => {
    if (!societe?.id) return;
    setActualisation(true);
    try {
      const res = await synchroniserQontoMaintenant(societe.id);
      if (!res.success) toast.error(res.error);
      else if (res.data.erreur) toast.error(res.data.erreur);
      else if (res.data.pointees.length) toast.success(`${res.data.pointees.length} facture(s) payée(s) automatiquement.`);
      await charger();
    } finally {
      setActualisation(false);
    }
  };

  const rattacher = async (virementId: string, factureId: string, numero: string) => {
    if (!societe?.id) return;
    setOccupe(`${virementId}:${factureId}`);
    try {
      const res = await rattacherVirement(societe.id, virementId, factureId);
      if (!res.success) toast.error(res.error);
      else {
        toast.success(
          `${res.data.dejaPayee ? `Virement lié à ${numero}` : res.data.payee ? `Facture ${numero} payée` : `Paiement partiel enregistré sur ${numero}`}${res.data.justificatif ? " · PDF joint dans Qonto" : ""}.`
        );
        await charger();
      }
    } finally {
      setOccupe(null);
    }
  };

  const rattraper = async (debut: string, fin: string) => {
    if (!societe?.id) return;
    const res = await rattraperQontoMaintenant(societe.id, debut, fin);
    if (!res.success) toast.error(res.error);
    else if (res.data.erreur) toast.error(res.data.erreur);
    else toast.success(`${res.data.liees.length} facture(s) liée(s) à leur virement · ${res.data.justificatifs.joints} PDF joint(s) dans Qonto.`);
    await charger();
  };

  const ignorer = async (virementId: string) => {
    if (!societe?.id) return;
    setOccupe(`${virementId}:ignorer`);
    try {
      const res = await ignorerVirementQonto(societe.id, virementId);
      if (!res.success) toast.error(res.error);
      else await charger();
    } finally {
      setOccupe(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-28 sm:py-6 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Rattachement</h1>
          <p className="text-xs text-muted-foreground">
            Les virements reçus sur Qonto que MyFacture n'a pas rattachés seul.
            {etat?.derniereSynchro ? ` Dernier passage : ${new Date(etat.derniereSynchro).toLocaleString("fr-FR")}.` : ""}
          </p>
        </div>
        {etat?.connecte && (
          <button
            type="button"
            onClick={actualiser}
            disabled={actualisation}
            className="shrink-0 inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-border text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            {actualisation ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="hidden sm:inline">Actualiser</span>
          </button>
        )}
      </div>

      {etat === null ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : !etat.connecte ? (
        <div className="glass-card p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 flex gap-3">
          <AlertCircle className="h-5 w-5 text-amber-400 shrink-0" />
          <p className="text-sm text-foreground">
            Aucun compte Qonto n'est connecté pour cette société.{" "}
            <Link href="/settings?view=QONTO" className="text-blue-500 font-medium">Connecter Qonto</Link>
          </p>
        </div>
      ) : (
        <>
          {etat.erreur && (
            <div className="glass-card p-4 rounded-xl border border-red-500/30 bg-red-500/5 text-sm text-red-400">{etat.erreur}</div>
          )}
          <BlocRattrapage rattrapage={etat.rattrapage} onLancer={rattraper} onRattacher={rattacher} occupe={occupe} />

          {etat.virements.length === 0 ? (
            <div className="glass-card p-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5 flex flex-col items-center gap-2 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              <p className="text-sm font-medium text-foreground">Tout est rattaché.</p>
              <p className="text-xs text-muted-foreground">Aucun virement reçu ces 120 derniers jours n'attend de facture.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {etat.virements.map((v) => (
                <CarteVirement key={v.virementId} v={v} factures={etat.factures} occupe={occupe} onRattacher={rattacher} onIgnorer={ignorer} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
